import { AdvancedMarker, APIProvider, InfoWindow, Map } from '@vis.gl/react-google-maps';
import { addMonths, endOfMonth, startOfMonth } from 'date-fns';
import { useEffect, useState } from 'react';
import { Col, Container, Form, Row, Spinner } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import { Filters } from '../../../../components/calendar/SFCCalendar/Filters';
import { Event, SFCCalendar } from '../../../../components/calendar/SFCCalendar/SFCCalendar';
import { SFCMobileCalendar } from '../../../../components/calendar/SFCCalendar/SFCMobileCalendar';
import { useToast } from '../../../../context/ToastContext';
import {
  useGetCalendarLessonsQuery,
  useGetLessonsCountQuery
} from '../../../../services/endpoints/schedule/lesson';
import { getCurrentUser, getPreferredLocation, LatLng } from '../../../../services/helper';
import { GetQueryParams } from '../../../../types/api.type';
import { eventColors, eventTypeMapping, eventTypes } from '../../../../utils/consts/index';
import { dateInTimeZone, formatDate, storedSessionDateTimeToInstant } from '../../../../utils/dateUtils';
import './CalendarView.css';

interface VenueMarker {
  id: string | number;
  lat: number;
  lng: number;
  label: string;
  venue: {
    id: number;
    title: string;
    address?: string;
    city?: string;
    state?: string;
    zip?: string;
    location_id?: number;
  };
}

interface VenueMarkerProps {
  marker: VenueMarker;
  onClick: (marker: VenueMarker) => void;
}

interface VenueInfoWindowProps {
  marker: VenueMarker;
  onClose: () => void;
}

const VenueMarkerComponent: React.FC<VenueMarkerProps> = ({ marker, onClick }) => {
  return (
    <AdvancedMarker
      key={marker.id}
      position={{ lat: marker.lat, lng: marker.lng }}
      onClick={() => onClick(marker)}
    >
      <button
        className="marker-button"
        type="button"
      >
        <img
          src={EMBASSY_ICON_URL}
          alt="Venue Icon"
          className="marker-icon"
        />
        <span className="marker-label">{marker.label}</span>
      </button>
    </AdvancedMarker>
  );
};

const VenueInfoWindow: React.FC<VenueInfoWindowProps> = ({ marker, onClose }) => {
  const navigate = useNavigate();

  const googleMapsUrl = `https://www.google.com/maps?q=${encodeURIComponent(
    marker.venue?.address || marker.label
  )}`;

  const handleTitleClick = () => {
    if (marker.venue?.location_id) {
      navigate(`/admin/places/locations/${marker.venue.location_id}`);
      onClose();
    }
  };

  return (
    <InfoWindow
      position={{ lat: marker.lat, lng: marker.lng }}
      onCloseClick={onClose}
      pixelOffset={INFO_WINDOW_OFFSET}
    >
      <div className="info-window-container">
        <h6
          className="info-window-title info-window-title-clickable"
          onClick={handleTitleClick}
          title={
            marker.venue?.location_id
              ? 'Click to view location details'
              : 'Location details not available'
          }
        >
          {marker.label}
        </h6>

        {marker.venue?.address && (
          <p className="info-window-detail">
            <strong>Address:</strong> {marker.venue.address}
          </p>
        )}

        {marker.venue?.city && marker.venue?.state && (
          <p className="info-window-detail">
            <strong>Location:</strong> {marker.venue.city}, {marker.venue.state}
          </p>
        )}

        <div className="info-window-divider" />

        <a
          href={googleMapsUrl}
          target="_blank"
          rel="noopener noreferrer"
          className="info-window-link"
        >
          View on Google Maps
        </a>
      </div>
    </InfoWindow>
  );
};

const EventKeys = () => (
  <div className="rounded-3 px-lg-3 py-lg-2 event-keys-container mt-3 border p-2">
    <span className="text-info text-uppercase fw-semibold">Key</span>
    <Row className="align-items-center">
      {Object.keys(eventTypes).map((type: string) => {
        const key = type as keyof typeof eventTypes;
        return (
          <Col
            key={type}
            xs={12}
            lg="auto"
            className="d-flex align-items-center me-4"
          >
            <div
              className="rounded-circle event-key-indicator me-1"
              style={{ background: eventColors[key] }}
            ></div>
            <span>{eventTypes[key]}</span>
          </Col>
        );
      })}
      <Col
        xs={12}
        lg="auto"
        className="d-flex align-items-center me-4"
      >
        <div
          className="event-key-indicator me-1"
          style={{
            background: `${eventColors.mainInstructor}18`,
            border: `2px dashed ${eventColors.mainInstructor}`,
            borderRadius: 4
          }}
        />
        <span>Sub requested</span>
      </Col>
    </Row>
  </div>
);

const defaultFilters = [
  {
    operator: 'greater_equal_than',
    field: 'date',
    value: formatDate(dateInTimeZone(startOfMonth(addMonths(new Date(), -6))), 'yyyy-MM-dd')
  },
  {
    operator: 'lesser_equal_than',
    field: 'date',
    value: formatDate(dateInTimeZone(endOfMonth(addMonths(new Date(), 6))), 'yyyy-MM-dd')
  }
];

const GOOGLE_MAPS_API_KEY = process.env.REACT_APP_GOOGLE_API_KEY || 'undefined';
const EMBASSY_ICON_URL = 'https://cdn-icons-png.flaticon.com/128/684/684908.png';
const DEFAULT_US_CENTER: LatLng = { lat: 39.8283, lng: -98.5795 };
const DEFAULT_ZOOM = 4;
const USER_LOCATION_ZOOM = 8;
const INFO_WINDOW_OFFSET: [number, number] = [0, -50];

const createVenueMarker = (
  lesson: any,
  courseId: number,
  lessonId: number,
  idx: number
): VenueMarker => ({
  id: `${courseId}-${lessonId}-${idx}`,
  lat: lesson.course.venue.latitude,
  lng: lesson.course.venue.longitude,
  label: lesson.course.venue.title,
  venue: {
    id: lesson.course.venue.id,
    title: lesson.course.venue.title,
    address: lesson.course.venue.address,
    city: lesson.course.venue.city,
    state: lesson.course.venue.state,
    zip: lesson.course.venue.zip,
    location_id: lesson.course.venue.location_id
  }
});

const getMapCenter = (userLocation: { location: LatLng } | null): LatLng =>
  userLocation?.location || DEFAULT_US_CENTER;

const getMapZoom = (userLocation: { location: LatLng } | null): number =>
  userLocation?.location ? USER_LOCATION_ZOOM : DEFAULT_ZOOM;

export const CalendarView = () => {
  const currentUser = getCurrentUser();
  const isAdminUser = currentUser?.isAdmin;
  const userLocation = getPreferredLocation();

  const [filters, setFilters] = useState<GetQueryParams>({
    data: {
      filterBy: [
        ...defaultFilters,
        ...(!isAdminUser || currentUser?.isInstructor
          ? [
              {
                operator: 'equals',
                field: 'instructed_id',
                value: String(currentUser?.id)
              }
            ]
          : [])
      ]
    }
  });

  const [events, setEvents] = useState<Event[]>([]);
  const [dates, setDates] = useState<string[]>([]);
  const [showFullEvents, setShowFullEvents] = useState<boolean>(
    !isAdminUser || currentUser?.isInstructor
  );
  const [fetchEvents, setFetchEvents] = useState<boolean>(
    !isAdminUser || currentUser?.isInstructor
  );

  // COMMENTED OUT - Old hook for instructor lessons (kept available for other components)
  // const { data: lessons, isFetching: lessonsFetching, refetch:fetchLessons } = useGetInstructorLessonsQuery(filters, { skip: !fetchEvents});

  // NEW HOOK - Calendar lessons endpoint
  const {
    data: lessons,
    isFetching: lessonsFetching,
    refetch: fetchLessons
  } = useGetCalendarLessonsQuery(filters, { skip: !fetchEvents });

  const locationFilter = filters?.data?.filterBy?.find((f) => f.field === 'location_id');
  const territoryFilter = filters?.data?.filterBy?.find((f) => f.field === 'territory_id');
  const instructorFilter = filters?.data?.filterBy?.find((f) => f.field === 'instructed_id');
  const filteredInstructorId = instructorFilter ? parseInt(instructorFilter.value as string) : null;
  const linkParams =
    locationFilter && Number(locationFilter.value)
      ? `location_id=${Number(locationFilter.value)}`
      : '';
  const { data, error, isLoading, isFetching } = useGetLessonsCountQuery(
    {
      start: dates[0],
      end: dates[1],
      ...(locationFilter &&
        Number(locationFilter?.value) && { location_id: Number(locationFilter.value) }),
      ...(territoryFilter &&
        Number(territoryFilter?.value) && { territory_id: Number(territoryFilter.value) })
    },
    {
      skip:
        fetchEvents ||
        showFullEvents ||
        !isAdminUser ||
        dates[0] === undefined ||
        dates[1] === undefined
    }
  );
  const { addToast } = useToast();
  const [position, setPosition] = useState<LatLng>(getMapCenter(userLocation));
  const [markers, setMarkers] = useState<VenueMarker[]>([]);
  const [showVenues, setShowVenues] = useState<boolean>(true);
  const [selectedMarker, setSelectedMarker] = useState<VenueMarker | null>(null);
  const zoom = getMapZoom(userLocation);

  useEffect(() => {
    if (error && !isLoading) {
      addToast('Error while loading events', 'error');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, error, isLoading]);

  useEffect(() => {
    if (fetchEvents) {
      fetchLessons();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // COMMENTED OUT - No longer needed since backend returns filtered lessons by date
  // const getWeekdayNumber = (dayName: string) => {
  //   const days = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"];
  //   return days.indexOf(dayName);
  // };

  // Local calendar-date helper (no TZ reinterpretation). Used for count-dot
  // placeholders and as FancyTime decode fallback.
  const dateOnlyLocalAt = (dateStr: string, hours = 12, minutes = 0) => {
    const [y, mo, d] = String(dateStr).slice(0, 10).split('-').map(Number);
    return new Date(y, (mo || 1) - 1, d || 1, hours, minutes, 0);
  };

  const generateDailyEvents = (lesson: any) => {
    // NEW SIMPLIFIED VERSION - Since backend returns filtered lessons by date
    // we only need to create one event for the specific lesson date
    return [
      {
        date: lesson.date, // Use the specific lesson date from backend
        startTime: lesson.calculated_starts_at
          ? lesson.calculated_starts_at
          : lesson.course.starts_at,
        endTime: lesson.calculated_ends_at ? lesson.calculated_ends_at : lesson.course.ends_at
      }
    ];

    // COMMENTED OUT - Original logic that generated multiple events for date range
    // const course = lesson.course;
    // const events = [];
    // const startDate = new Date(course.starts_on);
    // const endDate = new Date(course.ends_on);
    // const dayOfWeek = getWeekdayNumber(lesson.date_day_name); // Convert "Fridays" -> "Friday"

    // // Parse no class dates
    // const noClassDates = course.no_class
    //   ? course.no_class.split(",").map((date: string) => new Date(`${startDate.getFullYear()}-${date.trim()}`))
    //   : [];

    // let currentDate = new Date(startDate);

    // // Loop through dates and collect only Fridays
    // while (currentDate <= endDate) {
    //   if (currentDate.getDay() === dayOfWeek && !noClassDates.some((date: Date) => date.getTime() === currentDate.getTime())) {
    //     events.push({
    //       date: currentDate.toISOString().split("T")[0], // Format YYYY-MM-DD
    //       startTime: lesson.calculated_starts_at ? lesson.calculated_starts_at : course.starts_at,
    //       endTime: lesson.calculated_ends_at ? lesson.calculated_ends_at : course.ends_at
    //     });
    //   }
    //   // Move to the next day
    //   currentDate.setDate(currentDate.getDate() + 1);
    // }

    // return events;
  };

  useEffect(() => {
    if (lessons) {
      const lessonsEvents: Event[] = [];
      const newMarkers: VenueMarker[] = [];

      // UPDATED - Process lessons directly since new endpoint returns array instead of grouped structure
      lessons.forEach((lesson, idx) => {
        let dailyEvents = generateDailyEvents(lesson);
        const venueTz = (lesson.course?.venue as any)?.location?.timezone;
        dailyEvents.forEach((dailyEvent) => {
          lessonsEvents.push({
            title: lesson.course.title,
            // Decode FancyTime-encoded starts/ends against the venue timezone so
            // Pacific afternoon classes stored as 00:00:00 land on the correct day.
            start:
              storedSessionDateTimeToInstant(dailyEvent.date, dailyEvent.startTime, venueTz) ??
              dateOnlyLocalAt(dailyEvent.date),
            end:
              storedSessionDateTimeToInstant(dailyEvent.date, dailyEvent.endTime, venueTz) ??
              dateOnlyLocalAt(dailyEvent.date),
            type: eventTypeMapping['main_instructor_id'], // Using default type since we don't have label grouping anymore
            lesson
          });
        });

        if (lesson?.course?.venue?.latitude && lesson?.course?.venue?.longitude) {
          newMarkers.push(createVenueMarker(lesson, lesson.course.id, lesson.id, idx));
        }
      });

      // COMMENTED OUT - Old processing logic for grouped lessons
      // lessons.forEach(async (item: any) => {
      //   item.lessons.forEach((lesson: any, idx: number) => {
      //     let dailyEvents = generateDailyEvents(lesson);
      //     dailyEvents.map((dailyEvent) => {
      //       lessonsEvents.push({
      //         title: lesson.course.title,
      //         start: new Date(`${dailyEvent.date} ${dailyEvent.startTime}`),
      //         end: new Date(`${dailyEvent.date} ${dailyEvent.endTime}`),
      //         type: eventTypeMapping[item.label!],
      //         lesson
      //       })
      //     })

      //     if (lesson?.course?.venue?.latitude && lesson?.course?.venue?.longitude) {
      //       newMarkers.push(createVenueMarker(lesson, lesson.course.id, lesson.id, idx));
      //     }
      //   })
      // });

      const uniqueLessonsEvents = lessonsEvents.filter(
        (event, index, self) =>
          self.findIndex(
            (e2) =>
              e2.start.getTime() === event.start.getTime() &&
              e2.end.getTime() === event.end.getTime() &&
              e2.title === event.title &&
              e2.type === event.type
          ) === index
      );

      const uniqueMarkers = newMarkers.filter(
        (marker, index, self) => self.findIndex((m) => m.venue.id === marker.venue.id) === index
      );

      setFetchEvents(false);
      setEvents(uniqueLessonsEvents);
      setShowFullEvents(true);
      setMarkers(uniqueMarkers);
    }
  }, [lessons]);

  useEffect(() => {
    if (data && !showFullEvents) {
      const lessons: Event[] = [];
      data.lessonsCount.forEach((item) => {
        // Date-only count markers — local wall clock, not FancyTime.
        const start = dateOnlyLocalAt(item.lesson_date, 0);
        const end = dateOnlyLocalAt(item.lesson_date, 23);

        for (let index = 0; index < item.count_all; index++) {
          lessons.push({
            title: '',
            start,
            end,
            type: eventTypeMapping['main_instructor_id']
          });
        }
      });
      setEvents(lessons);
    }
  }, [data, showFullEvents]);

  useEffect(() => {
    if (filters && filters.data?.filterBy) {
      let filterArr = filters.data.filterBy;
      const newDates: string[] = filterArr
        .filter((filter) => filter.field === 'date')
        .map((filter) => filter.value as string);
      setDates(newDates);
      const fetchEventFlag = filterArr?.find((f) => f.field === 'instructed_id') !== undefined;
      setFetchEvents(fetchEventFlag);
      setShowFullEvents(fetchEventFlag);
    }
  }, [filters]);

  const setFilterBy = (filterBy: { operator: string; field: string; value: string }[]) => {
    setFilters((prev) => ({
      ...prev,
      data: {
        ...prev!.data,
        filterBy
      }
    }));
  };

  const calendarLoading = lessonsFetching || isLoading || isFetching;

  const CalendarLoadingOverlay = () =>
    calendarLoading ? (
      <div
        className="calendar-loading-overlay"
        role="status"
        aria-live="polite"
        aria-label="Loading calendar"
      >
        <Spinner animation="border" variant="primary" />
        <span className="text-secondary fw-semibold">Loading calendar...</span>
      </div>
    ) : null;

  return (
    <div className="px-lg-5 px-0">
      <h2 className="d-block d-lg-none text-secondary text-uppercase calendar-header-mobile px-3 py-2">
        Calendar
      </h2>
      <h2 className="d-none d-lg-block text-info fw-bold calendar-header-desktop ps-2 pt-4">
        Calendar
      </h2>

      <Container fluid>
        <Filters
          onApplyFilters={setFilterBy}
          disabled={calendarLoading}
          loading={calendarLoading}
        />
        {/* Mobile calendar */}
        <div className="d-block d-lg-none mb-4 calendar-loading-container">
          <CalendarLoadingOverlay />
          <SFCMobileCalendar
            events={events}
            showFullEvents={showFullEvents}
            currentDates={dates}
            filteredInstructorId={filteredInstructorId}
          />
          <EventKeys />
        </div>

        {/* Desktop */}
        <div className="d-none d-lg-block rounded-4 calendar-desktop-container mb-5 p-5 calendar-loading-container">
          <CalendarLoadingOverlay />
          <SFCCalendar
            events={events}
            showFullEvents={showFullEvents}
            currentDates={dates}
            filteredInstructorId={filteredInstructorId}
            linkParams={linkParams}
          />
          <EventKeys />
        </div>
        <APIProvider apiKey={GOOGLE_MAPS_API_KEY}>
          <div className="calendar-map-container">
            <Form className="calendar-map-form">
              <div className="calendar-map-checkbox">
                <Form.Check
                  inline
                  label="Venues"
                  name="group1"
                  type="checkbox"
                  checked={showVenues}
                  onChange={() => setShowVenues(!showVenues)}
                />
              </div>
            </Form>
            <Map
              mapId="7914a7f3b7361cce"
              defaultCenter={position}
              defaultZoom={zoom}
              gestureHandling="greedy"
              disableDefaultUI={false}
              onClick={(e) => {
                if (e.detail.latLng) {
                  setPosition({
                    lat: e.detail.latLng.lat,
                    lng: e.detail.latLng.lng
                  });
                  setSelectedMarker(null);
                }
              }}
            >
              {/* Render markers */}
              {showVenues &&
                markers.length > 0 &&
                markers.map((marker) => (
                  <VenueMarkerComponent
                    key={marker.id}
                    marker={marker}
                    onClick={setSelectedMarker}
                  />
                ))}

              {selectedMarker && (
                <VenueInfoWindow
                  marker={selectedMarker}
                  onClose={() => setSelectedMarker(null)}
                />
              )}
            </Map>
          </div>
        </APIProvider>
      </Container>
    </div>
  );
};
